// src/services/email/templates/unifiedEmail.ts

import { EmailTemplate, EmailVariables } from '../types';
import { signaturTelefon } from '../signaturTelefon';

/**
 * Berechnet den Text für die DUO-Verfügbarkeit basierend auf der aktuellen Zeit.
 * Lohnläufe finden werktags (Mo-Fr) statt: 8:30, 10:00, 11:30, 13:00, 14:30, 16:00, 18:30
 * Daten sind 30 Minuten nach dem Lohnlauf verfügbar.
 */
const getDuoAvailabilityText = (anrede: 'Du' | 'Sie'): string => {
  // Aktuelle Zeit in deutscher Zeitzone (Europe/Berlin) - berücksichtigt Sommer-/Winterzeit
  const now = new Date();
  const germanFormatter = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'long',
    hour12: false
  });
  
  const parts = germanFormatter.formatToParts(now);
  const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
  const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
  const weekday = parts.find(p => p.type === 'weekday')?.value || '';
  
  const currentMinutes = hour * 60 + minute;
  
  // Lohnlauf-Zeiten in Minuten seit Mitternacht
  const lohnlaeufe = [
    { start: 8 * 60 + 30, available: '9:00' },      // 8:30 → 9:00
    { start: 10 * 60, available: '10:30' },          // 10:00 → 10:30
    { start: 11 * 60 + 30, available: '12:00' },     // 11:30 → 12:00
    { start: 13 * 60, available: '13:30' },          // 13:00 → 13:30
    { start: 14 * 60 + 30, available: '15:00' },     // 14:30 → 15:00
    { start: 16 * 60, available: '16:30' },          // 16:00 → 16:30
    { start: 18 * 60 + 30, available: '19:00' }      // 18:30 → 19:00
  ];
  
  // Wochenende prüfen
  const isWeekend = weekday === 'Samstag' || weekday === 'Sonntag';
  
  // Freitag prüfen (für "ab Montag" Logik)
  const isFriday = weekday === 'Freitag';
  
  // Du/Sie Formulierungen - korrekte deutsche Satzstellung
  const sieDu = anrede === 'Du' ? 'Du' : 'Sie';
  const findeForm = anrede === 'Du' ? 'findest' : 'finden';
  const ihrem = anrede === 'Du' ? 'Deinem' : 'Ihrem';
  
  // Am Wochenende → immer Montag 9:00
  if (isWeekend) {
    return `${sieDu} ${findeForm} alle abrechnungsrelevanten Dokumente ab Montag, 9:00 Uhr, in ${ihrem} DATEV Unternehmen Online.`;
  }
  
  // Nächsten verfügbaren Lohnlauf finden
  for (const lauf of lohnlaeufe) {
    if (currentMinutes < lauf.start) {
      // Nächster Lohnlauf ist heute
      return `${sieDu} ${findeForm} alle abrechnungsrelevanten Dokumente ab ${lauf.available} Uhr in ${ihrem} DATEV Unternehmen Online.`;
    }
  }
  
  // Nach dem letzten Lohnlauf (18:30) → nächster Werktag
  if (isFriday) {
    return `${sieDu} ${findeForm} alle abrechnungsrelevanten Dokumente ab Montag, 9:00 Uhr, in ${ihrem} DATEV Unternehmen Online.`;
  } else {
    return `${sieDu} ${findeForm} alle abrechnungsrelevanten Dokumente ab morgen, 9:00 Uhr, in ${ihrem} DATEV Unternehmen Online.`;
  }
};

export const unifiedEmailTemplate: EmailTemplate = {
  id: 'unified-email',
  name: 'Unified Email Template',
  
  subject: (vars: EmailVariables) => {
    const prefix = vars.testPrefix || '';
    return `${prefix}XTAX - Lohnabrechnung ${vars.customerName} - ${vars.month} ${vars.year}`;
  },
  
  bodyText: (vars: EmailVariables) => {
    const getGreeting = () => {
      if (vars.anrede === 'Du') {
        return vars.vorname ? `Hallo ${vars.vorname}` : `Hallo ${vars.customerName}`;
      } else {
        const anrede = vars.geschlecht === 'weiblich' ? 'Sehr geehrte Frau' : 'Sehr geehrter Herr';
        return vars.nachname ? `${anrede} ${vars.nachname}` : `Sehr geehrte Damen und Herren`;
      }
    };

    const getSenderName = () => vars.senderIdentity?.fullName || 'Tim Wolter';
    
    const getImAuftragVonText = () => {
      if (!vars.imAuftragVon) return '';
      
      if (vars.imAuftragVon === 'OPS') {
        return ' im Auftrag der OPS Steuerberatungsgesellschaft mbH';
      } else if (vars.imAuftragVon === 'PROFIL LK') {
        return ' im Auftrag der PROFIL LK Steuerberatungsgesellschaft mbH';
      }
      return '';
    };

    // NEUE FUNKTION: Custom Message mit Einleitung und Einrückung
    const getCustomMessageSection = () => {
      if (!vars.customMessage || !vars.customMessage.trim()) {
        return '';
      }
      
      const einleitung = vars.anrede === 'Du' 
        ? 'Wir haben in diesem Monat folgende Fragen und Hinweise für Dich:'
        : 'Wir haben in diesem Monat folgende Fragen und Hinweise für Sie:';
      
      // Rücke jede Zeile der Custom Message ein (4 Leerzeichen)
      const eingerueckteMessage = vars.customMessage.trim()
        .split('\n')
        .map(line => `    ${line}`)
        .join('\n');
      
      return `\n\n${einleitung}\n\n${eingerueckteMessage}`;
    };

    const getDirForm = () => vars.anrede === 'Du' ? 'Dir' : 'Ihnen';
    const getFindeForm = () => vars.anrede === 'Du' ? 'findest Du' : 'finden Sie';

    const getBodyContent = () => {
      if (vars.isDuoNotification) {
        // Bei DUO: Prüfen ob es zusätzliche individuelle Anhänge gibt
        const hasIndividualAttachments = (vars.individualAttachments && vars.individualAttachments.length > 0);
        
        // GEÄNDERT: Dynamische Verfügbarkeitszeit mit Du/Sie-Differenzierung
        let content = getDuoAvailabilityText(vars.anrede === 'Du' ? 'Du' : 'Sie');
        
        // Wenn es individuelle Anhänge gibt, füge einen generischen Hinweis hinzu
        if (hasIndividualAttachments) {
          content += vars.anrede === 'Du'
            ? '\n\nIm Anhang findest Du weitere Dateien für den aktuellen Abrechnungslauf.\n'
            : '\n\nIm Anhang finden Sie weitere Dateien für den aktuellen Abrechnungslauf.\n';
        }
        
        return content;
      } else {
        let content = `Im Anhang ${getFindeForm()} alle abrechnungsrelevanten Dokumente für die Lohnbuchhaltung.\n`;
        
        // Bei normalem E-Mail-Versand: Download-Links anzeigen falls vorhanden
        if (vars.downloadLinks && vars.downloadLinks.length > 0) {
          content += '\n\nFolgende Dateien stehen zum Download bereit:\n';
          vars.downloadLinks.forEach(link => {
            const fileName = link.fileName || link.filename || 'Datei';
            content += `- ${fileName}\n`;
          });
        }
        
        return content;
      }
    };
    
    // NEUE STRUKTUR: Custom Message NACH Unterlagen, VOR Rückfragen
    return `${getGreeting()},

wir haben die Abrechnungsunterlagen für ${vars.month} ${vars.year}${getImAuftragVonText()} fertiggestellt.

${getBodyContent()}${getCustomMessageSection()}
${!vars.customMessage || !vars.customMessage.trim() ? '\n' : ''}
Für Rückfragen oder bei Anpassungsbedarf stehen wir ${getDirForm()} jederzeit gerne zur Verfügung.

Beste Grüße
${getSenderName()}

--
XTAX GmbH & Co. KG
${getSenderName()}
${vars.senderIdentity?.title || 'Geschäftsführender Gesellschafter'}`;
  },

  bodyHtml: (vars: EmailVariables) => {
    const getGreeting = () => {
      if (vars.anrede === 'Du') {
        return vars.vorname ? `Hallo ${vars.vorname}` : `Hallo ${vars.customerName}`;
      } else {
        const anrede = vars.geschlecht === 'weiblich' ? 'Sehr geehrte Frau' : 'Sehr geehrter Herr';
        return vars.nachname ? `${anrede} ${vars.nachname}` : `Sehr geehrte Damen und Herren`;
      }
    };

    const getDirForm = () => vars.anrede === 'Du' ? 'Dir' : 'Ihnen';
    const getFindeForm = () => vars.anrede === 'Du' ? 'findest Du' : 'finden Sie';
    
    const getImAuftragVonText = () => {
      if (!vars.imAuftragVon) return '';
      
      if (vars.imAuftragVon === 'OPS') {
        return ' im Auftrag der OPS Steuerberatungsgesellschaft mbH';
      } else if (vars.imAuftragVon === 'PROFIL LK') {
        return ' im Auftrag der PROFIL LK Steuerberatungsgesellschaft mbH';
      }
      return '';
    };

    // NEUE FUNKTION: Custom Message HTML mit Einrückung und Markdown-Processing
    const getCustomMessageHtml = () => {
      if (!vars.customMessage || !vars.customMessage.trim()) {
        return '';
      }
      
      const einleitung = vars.anrede === 'Du' 
        ? 'Wir haben in diesem Monat folgende Fragen und Hinweise für Dich:'
        : 'Wir haben in diesem Monat folgende Fragen und Hinweise für Sie:';
      
      // Verarbeite Markdown und HTML-Formatierung
      const processedMessage = vars.customMessage.trim()
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // **Text** → <strong>Text</strong>
        .replace(/\n/g, '<br>'); // Zeilenumbrüche → <br>
      
      return `<br>${einleitung}<br><br><div style="margin-left: 20px; padding-left: 10px; border-left: 3px solid #e1e5f2; color: #555;">${processedMessage}</div>`;
    };

    const getBodyContent = () => {
      if (vars.isDuoNotification) {
        // Bei DUO: Prüfen ob es zusätzliche individuelle Anhänge gibt
        const hasIndividualAttachments = (vars.individualAttachments && vars.individualAttachments.length > 0);
        
        // GEÄNDERT: Dynamische Verfügbarkeitszeit mit Du/Sie-Differenzierung
        let content = getDuoAvailabilityText(vars.anrede === 'Du' ? 'Du' : 'Sie');
        
        // Wenn es individuelle Anhänge gibt, füge einen generischen Hinweis hinzu
        if (hasIndividualAttachments) {
          content += vars.anrede === 'Du'
            ? '<br><br>Im Anhang findest Du weitere Dateien für den aktuellen Abrechnungslauf.'
            : '<br><br>Im Anhang finden Sie weitere Dateien für den aktuellen Abrechnungslauf.';
        }
        
        // Konsistent mit <br> enden für einheitliche Leerzeile danach
        return content + '<br>';
      } else {
        let content = `Im Anhang ${getFindeForm()} alle abrechnungsrelevanten Dokumente für die Lohnbuchhaltung.`;
        
        // Bei normalem E-Mail-Versand: Download-Links anzeigen falls vorhanden
        if (vars.downloadLinks && vars.downloadLinks.length > 0) {
          // Download-Box ist ein Block-Element (<div>) - erzeugt eigenen Zeilenumbruch
          content = `${content}<br>${generateDownloadLinksHtml()}`;
          return content;
        }
        
        // Ohne Download-Links: <br> nötig, da Inline-Text keinen eigenen Umbruch erzeugt
        return content + '<br>';
      }
    };

    const generateDownloadLinksHtml = () => {
      if (!vars.downloadLinks || vars.downloadLinks.length === 0) {
        return '';
      }

      const linksHtml = vars.downloadLinks
        .map(link => {
          const fileName = link.fileName || link.filename || 'Datei';
          return `<a href="${link.url}" class="download-link" target="_blank">${fileName}</a>`;
        })
        .join('');

      return `
        <div class="download-section">
          <strong>Folgende Dateien stehen zum Download bereit:</strong><br>
          ${linksHtml}
        </div>`;
    };

    // ORIGINALE SIGNATUR - EXAKT wie bereitgestellt
    const generateSignature = (): string => {
      const sender = vars.senderIdentity;
      
      // Fallback auf Tim Wolter wenn kein Sender gewählt
      if (!sender) {
        return generateTimWolterSignature();
      }

      // Verwende nur echte Foto-URL - KEINE Fallbacks auf Avatare
      const photoUrl = sender.photoUrl;

      // KRITISCH: Wenn kein Foto vorhanden ist, soll der E-Mail-Versand fehlschlagen
      if (!photoUrl) {
        throw new Error(`Kein Foto verfügbar für ${sender.fullName}. E-Mail-Versand abgebrochen.`);
      }

      // Konvertiere relative URLs zu absoluten URLs für E-Mail-Kompatibilität
      const absolutePhotoUrl = photoUrl.startsWith('http') 
        ? photoUrl 
        : `https://intranet.xtax.de${photoUrl}`;

      // Verwende die ORIGINALE Signatur-Struktur mit dynamischen Werten
      return `
        <div dir="ltr" style="font-size:1px;direction:ltr">
          <table dir="ltr" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;width:100%;direction:ltr;border-collapse:collapse;font-size:1px">
            <tbody>
              <tr style="font-size:0">
                <td align="left" style="vertical-align:top">
                  <table cellpadding="0" cellspacing="0" border="0" style="width:0;border-collapse:collapse;font-size:0">
                    <tbody>
                      <tr style="font-size:0">
                        <td align="left" style="padding:0 29px 0 0;vertical-align:top">
                          <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0;line-height:normal">
                            <tbody>
                              <tr style="font-size:0">
                                <td align="right" style="vertical-align:top">
                                  <img width="104" height="104" border="0" alt="${sender.fullName}" style="width:104px;min-width:104px;max-width:104px;height:104px;min-height:104px;max-height:104px;font-size:0" src="${absolutePhotoUrl}">
                                </td>
                                <td><span style="font-family:remialcxesans;font-size:1px;color:#ffffff;line-height:1px"><span style="font-family:'template-qpQYLYoMEe-EdAAiSIGong'"></span><span style="font-family:'zone-1'"></span><span style="font-family:'zones-AQ'"></span></span></td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                        <td align="left" style="padding:0 43px 0 0;vertical-align:middle">
                          <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0;color:#181823;font-style:normal;font-weight:700;white-space:nowrap">
                            <tbody>
                              <tr style="font-size:21.33px">
                                <td align="left" style="padding:0;vertical-align:top;font-family:Calibri,Arial,sans-serif">${sender.fullName}</td>
                              </tr>
                              <tr style="font-size:12px;color:#000001">
                                <td align="left" style="padding:8px 0 0;vertical-align:top;font-family:Calibri,Arial,sans-serif">${sender.title?.toUpperCase() || 'STEUERBERATERIN'}</td>
                              </tr>
                              <tr style="font-size:0">
                                <td align="left" style="padding:11px 0 0;vertical-align:top">
                                  <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0">
                                    <tbody>
                                      <tr style="font-size:0">
                                        <td align="left" style="padding:0 32px 0 0;vertical-align:top">
                                          <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0;color:#3465de;font-style:normal;font-weight:400;line-height:normal;white-space:nowrap">
                                            <tbody>
                                              <tr style="font-size:12px">
                                                <td align="left" style="padding:0 5px 0 0;vertical-align:top;font-size:0"><img width="15" height="15" border="0" alt="" style="width:15px;min-width:15px;max-width:15px;height:15px;min-height:15px;max-height:15px;font-size:0" src="https://intranet.xtax.de/images/phone.png"></td>
                                                <td align="left" style="padding:0;vertical-align:top;font-family:Arial">${signaturTelefon(sender)}</td>
                                              </tr>
                                              <tr style="font-size:12px">
                                                <td align="left" style="padding:6px 5px 0 0;vertical-align:top;font-size:0"><img width="16" height="16" border="0" alt="" style="width:16px;min-width:16px;max-width:16px;height:16px;min-height:16px;max-height:16px;font-size:0" src="https://intranet.xtax.de/images/fax.png"></td>
                                                <td align="left" style="padding:6px 0 0;vertical-align:top;font-family:Arial">+49 221 6505419803</td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </td>
                                        <td align="left" style="padding:0;vertical-align:top">
                                          <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0;color:#3465de;font-style:normal;font-weight:400;line-height:normal;white-space:nowrap">
                                            <tbody>
                                              <tr style="font-size:12px">
                                                <td align="left" style="padding:0 5px 0 0;vertical-align:top;font-size:0"><img width="15" height="15" border="0" alt="" style="width:15px;min-width:15px;max-width:15px;height:15px;min-height:15px;max-height:15px;font-size:0" src="https://ci3.googleusercontent.com/mail-sig/AIorK4zX4wd6peH1xiEBkNrUYVSmjO1I6mRDpuorOd9f-6nQ4AvsgUautGW50nJHx8orAe3rTABEo55Cxo81"></td>
                                                <td align="left" style="padding:0;vertical-align:top;font-family:Arial"><a href="mailto:lohn@xtax.de" style="text-decoration:none;color:#3465de" target="_blank">lohn@xtax.de</a></td>
                                              </tr>
                                              <tr style="font-size:12px">
                                                <td align="left" style="padding:6px 5px 0 0;vertical-align:top;font-size:0"><img width="15" height="15" border="0" alt="" style="width:15px;min-width:15px;max-width:15px;height:15px;min-height:15px;max-height:15px;font-size:0" src="https://ci3.googleusercontent.com/mail-sig/AIorK4y9anEE4J-4lCmYR2G3eKu9QrImewZm8czjRvG3uHH8cVQk8dYmQ9hSzMqHKf24uPq47pyHaNhwNOVt"></td>
                                                <td align="left" style="padding:6px 0 0;vertical-align:top;font-family:Arial"><a href="https://www.xtax.de" target="_blank">https://www.xtax.de</a><br></td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                        <td align="right" style="line-height:normal;padding:0;vertical-align:middle">
                          <img width="271.31" height="100" border="0" alt="" style="width:271.31px;min-width:271.31px;max-width:271.31px;height:100px;min-height:100px;max-height:100px;font-size:0" src="https://intranet.xtax.de/images/xtax-logo.png">
                        </td>
                      </tr>
                      <tr style="font-size:0">
                        <td colspan="3" align="left" style="padding:0;vertical-align:top">
                          <table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;font-size:0">
                            <tbody>
                              <tr style="font-size:0">
                                <td align="left" style="vertical-align:top">
                                  <table cellpadding="0" cellspacing="0" border="0" style="white-space:normal;color:#c5c5c5;font-size:12px;font-family:Arial;font-weight:400;font-style:normal;text-align:justify;line-height:14.67px;width:100%;border-collapse:collapse">
                                    <tbody>
                                      <tr style="font-size:9px">
                                        <td style="font-family:Arial"><br>Die XTAX GmbH &amp; Co. KG ist als anerkannte Berufsausübungsgesellschaft bei der Steuerberaterkammer Köln registriert.<br>Amtsgericht Köln HRA 34389, USt-IdNr: DE327402879.<br>Sitz: Hansaring 78, 50670 Köln, Deutschland.<br>Geschäftsführende Gesellschafter: Eike Priel (Steuerberater), Tim Wolter.<br><br></td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                              <tr style="font-size:0">
                                <td align="left" style="vertical-align:top">
                                  <table cellpadding="0" cellspacing="0" border="0" style="white-space:normal;color:#c5c5c5;font-size:12px;font-family:Arial;font-weight:400;font-style:normal;text-align:justify;line-height:14.67px;width:100%;border-collapse:collapse">
                                    <tbody>
                                      <tr style="font-size:9px">
                                        <td style="font-family:Arial">Diese Nachricht ist ausschließlich für den Empfänger bestimmt. Sind Sie nicht der Empfänger, bitten wir um Nachricht und umgehende Löschung der Inhalte.<br>This message contains confidential information intended for a specific individual or entity as the intended recipient. If you are not the intended recipient, you are hereby notified that any distribution, any copying of this message in part or in whole, or any taking of action based on it, is strictly prohibited by law and may cause liability. In case you have received this message due to an error in transmission, we ask you to notify the sender immediately.<br></td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>`;
    };

    // Tim Wolter Fallback-Signatur
    const generateTimWolterSignature = () => {
      return `
        <div dir="ltr" style="font-size:1px;direction:ltr">
          <table dir="ltr" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;width:100%;direction:ltr;border-collapse:collapse;font-size:1px">
            <tbody>
              <tr style="font-size:0">
                <td align="left" style="vertical-align:top">
                  <table cellpadding="0" cellspacing="0" border="0" style="width:0;border-collapse:collapse;font-size:0">
                    <tbody>
                      <tr style="font-size:0">
                        <td align="left" style="padding:0 29px 0 0;vertical-align:top">
                          <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0;line-height:normal">
                            <tbody>
                              <tr style="font-size:0">
                                <td align="right" style="vertical-align:top">
                                  <img width="104" height="104" border="0" alt="Tim Wolter" style="width:104px;min-width:104px;max-width:104px;height:104px;min-height:104px;max-height:104px;font-size:0" src="https://intranet.xtax.de/images/staff/tim-wolter.jpg">
                                </td>
                                <td><span style="font-family:remialcxesans;font-size:1px;color:#ffffff;line-height:1px"><span style="font-family:'template-qpQYLYoMEe-EdAAiSIGong'"></span><span style="font-family:'zone-1'"></span><span style="font-family:'zones-AQ'"></span></span></td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                        <td align="left" style="padding:0 43px 0 0;vertical-align:middle">
                          <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0;color:#181823;font-style:normal;font-weight:700;white-space:nowrap">
                            <tbody>
                              <tr style="font-size:21.33px">
                                <td align="left" style="padding:0;vertical-align:top;font-family:Calibri,Arial,sans-serif">Tim Wolter</td>
                              </tr>
                              <tr style="font-size:12px;color:#000001">
                                <td align="left" style="padding:8px 0 0;vertical-align:top;font-family:Calibri,Arial,sans-serif">GESCHÄFTSFÜHRENDER GESELLSCHAFTER</td>
                              </tr>
                              <tr style="font-size:0">
                                <td align="left" style="padding:11px 0 0;vertical-align:top">
                                  <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0">
                                    <tbody>
                                      <tr style="font-size:0">
                                        <td align="left" style="padding:0 32px 0 0;vertical-align:top">
                                          <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0;color:#3465de;font-style:normal;font-weight:400;line-height:normal;white-space:nowrap">
                                            <tbody>
                                              <tr style="font-size:12px">
                                                <td align="left" style="padding:0 5px 0 0;vertical-align:top;font-size:0"><img width="15" height="15" border="0" alt="" style="width:15px;min-width:15px;max-width:15px;height:15px;min-height:15px;max-height:15px;font-size:0" src="https://intranet.xtax.de/images/phone.png"></td>
                                                <td align="left" style="padding:0;vertical-align:top;font-family:Arial">${signaturTelefon(null)}</td>
                                              </tr>
                                              <tr style="font-size:12px">
                                                <td align="left" style="padding:6px 5px 0 0;vertical-align:top;font-size:0"><img width="16" height="16" border="0" alt="" style="width:16px;min-width:16px;max-width:16px;height:16px;min-height:16px;max-height:16px;font-size:0" src="https://intranet.xtax.de/images/fax.png"></td>
                                                <td align="left" style="padding:6px 0 0;vertical-align:top;font-family:Arial">+49 221 6505419803</td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </td>
                                        <td align="left" style="padding:0;vertical-align:top">
                                          <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:0;color:#3465de;font-style:normal;font-weight:400;line-height:normal;white-space:nowrap">
                                            <tbody>
                                              <tr style="font-size:12px">
                                                <td align="left" style="padding:0 5px 0 0;vertical-align:top;font-size:0"><img width="15" height="15" border="0" alt="" style="width:15px;min-width:15px;max-width:15px;height:15px;min-height:15px;max-height:15px;font-size:0" src="https://ci3.googleusercontent.com/mail-sig/AIorK4zX4wd6peH1xiEBkNrUYVSmjO1I6mRDpuorOd9f-6nQ4AvsgUautGW50nJHx8orAe3rTABEo55Cxo81"></td>
                                                <td align="left" style="padding:0;vertical-align:top;font-family:Arial"><a href="mailto:lohn@xtax.de" style="text-decoration:none;color:#3465de" target="_blank">lohn@xtax.de</a></td>
                                              </tr>
                                              <tr style="font-size:12px">
                                                <td align="left" style="padding:6px 5px 0 0;vertical-align:top;font-size:0"><img width="15" height="15" border="0" alt="" style="width:15px;min-width:15px;max-width:15px;height:15px;min-height:15px;max-height:15px;font-size:0" src="https://ci3.googleusercontent.com/mail-sig/AIorK4y9anEE4J-4lCmYR2G3eKu9QrImewZm8czjRvG3uHH8cVQk8dYmQ9hSzMqHKf24uPq47pyHaNhwNOVt"></td>
                                                <td align="left" style="padding:6px 0 0;vertical-align:top;font-family:Arial"><a href="https://www.xtax.de" target="_blank">https://www.xtax.de</a><br></td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                        <td align="right" style="line-height:normal;padding:0;vertical-align:middle">
                          <img width="271.31" height="100" border="0" alt="" style="width:271.31px;min-width:271.31px;max-width:271.31px;height:100px;min-height:100px;max-height:100px;font-size:0" src="https://intranet.xtax.de/images/xtax-logo.png">
                        </td>
                      </tr>
                      <tr style="font-size:0">
                        <td colspan="3" align="left" style="padding:0;vertical-align:top">
                          <table cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;font-size:0">
                            <tbody>
                              <tr style="font-size:0">
                                <td align="left" style="vertical-align:top">
                                  <table cellpadding="0" cellspacing="0" border="0" style="white-space:normal;color:#c5c5c5;font-size:12px;font-family:Arial;font-weight:400;font-style:normal;text-align:justify;line-height:14.67px;width:100%;border-collapse:collapse">
                                    <tbody>
                                      <tr style="font-size:9px">
                                        <td style="font-family:Arial"><br>Die XTAX GmbH &amp; Co. KG ist als anerkannte Berufsausübungsgesellschaft bei der Steuerberaterkammer Köln registriert.<br>Amtsgericht Köln HRA 34389, USt-IdNr: DE327402879.<br>Sitz: Hansaring 78, 50670 Köln, Deutschland.<br>Geschäftsführende Gesellschafter: Eike Priel (Steuerberater), Tim Wolter.<br><br></td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                              <tr style="font-size:0">
                                <td align="left" style="vertical-align:top">
                                  <table cellpadding="0" cellspacing="0" border="0" style="white-space:normal;color:#c5c5c5;font-size:12px;font-family:Arial;font-weight:400;font-style:normal;text-align:justify;line-height:14.67px;width:100%;border-collapse:collapse">
                                    <tbody>
                                      <tr style="font-size:9px">
                                        <td style="font-family:Arial">Diese Nachricht ist ausschließlich für den Empfänger bestimmt. Sind Sie nicht der Empfänger, bitten wir um Nachricht und umgehende Löschung der Inhalte.<br>This message contains confidential information intended for a specific individual or entity as the intended recipient. If you are not the intended recipient, you are hereby notified that any distribution, any copying of this message in part or in whole, or any taking of action based on it, is strictly prohibited by law and may cause liability. In case you have received this message due to an error in transmission, we ask you to notify the sender immediately.<br></td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>`;
    };

    const getSenderName = () => vars.senderIdentity?.fullName || 'Tim Wolter';
    
    return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
        }
        .container {
          margin: 0;
          padding: 0;
        }
        .download-section {
          background-color: #f8f9ff;
          border: 1px solid #e1e5f2;
          border-radius: 6px;
          padding: 16px;
          margin: 16px 0;
        }
        .download-link {
          display: block;
          color: #3465de;
          text-decoration: none;
          font-weight: 500;
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .download-link:hover {
          text-decoration: underline;
        }
      </style>
    </head>
    <body>
      <div class="container">
        ${getGreeting()},<br>
        <br>
        wir haben die Abrechnungsunterlagen für ${vars.month} ${vars.year}${getImAuftragVonText()} fertiggestellt.<br>
        <br>
        ${getBodyContent()}${getCustomMessageHtml()}
        <br>
        Für Rückfragen oder bei Anpassungsbedarf stehen wir ${getDirForm()} jederzeit gerne zur Verfügung.<br>
        <br>
        Beste Grüße<br>
        ${getSenderName()}<br>
        <br>
        
        ${generateSignature()}
      </div>
    </body>
    </html>
    `;
  }
};
